/**
 * @file     DetailTabsShell.tsx
 * @readme   ../../CLAUDE.md
 * @purpose  Client tabs shell for the detail-page body card. Owns only the
 *           tab state; panel content arrives fully server-rendered via the
 *           `tabs` prop (ReactNode slots), so everything stays in the DOM
 *           (hidden attr) for SEO. Remote switches (e.g. the est.-monthly
 *           card) go through openDetailTab(key) — a window CustomEvent,
 *           same pattern DisclosureSection used.
 */
'use client';

import { layoutTokens } from '@/design-system/tokens/layout';
import * as React from 'react';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import useMediaQuery from '@mui/material/useMediaQuery';
import { alpha, useTheme } from '@mui/material/styles';
import { keyframes } from '@mui/material/styles';
import { glassBlur } from '@/design-system/tokens';
import { useAnalytics } from '@/hooks/useAnalytics';
import { analyticsEvents } from '@/core/constants/analytics';

const OPEN_TAB_EVENT = 'relta:detail-tab-open';
/** The same request on the slug page, where sections replace the tabs (ListingSectionNav listens). */
export const DETAIL_TAB_OPEN_EVENT = OPEN_TAB_EVENT;
const TRANSITION_MS = 200;

export const DETAIL_TAB_OVERVIEW = 'overview';
export const DETAIL_TAB_MORTGAGE = 'mortgage';

export function detailTabPanelId(key: string): string {
  return `detail-tabpanel-${key}`;
}

/** Switch the detail body card to this tab and scroll the card into view. */
export type DetailTabTrigger = 'tab_click' | 'monthly_estimate' | 'financial_breakdown' | 'programmatic';

export function openDetailTab(key: string, trigger: DetailTabTrigger = 'programmatic'): void {
  window.dispatchEvent(new CustomEvent(OPEN_TAB_EVENT, { detail: { key, trigger } }));
}

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

export interface DetailTab {
  key: string;
  label: string;
  content: React.ReactNode;
}

interface DetailTabsAnalytics {
  listingKey: string;
  surface: 'slug_page' | 'map_sheet';
}

export function DetailTabsShell({ tabs, analytics }: { tabs: DetailTab[]; analytics: DetailTabsAnalytics }) {
  const theme = useTheme();
  const { trackEvent } = useAnalytics();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [active, setActive] = React.useState(tabs[0]?.key ?? '');
  const rootRef = React.useRef<HTMLDivElement | null>(null);

  const selectTab = React.useCallback((key: string, trigger: DetailTabTrigger) => {
    if (!tabs.some((tab) => tab.key === key)) return;
    if (active === key) return;
    trackEvent({
      eventName: analyticsEvents.propertyDetailTabChanged,
      eventType: 'click',
      category: 'property_detail',
      label: key,
      metadata: {
        listing_key: analytics.listingKey,
        surface: analytics.surface,
        from_tab: active,
        to_tab: key,
        trigger,
      },
    });
    setActive(key);
  }, [active, analytics.listingKey, analytics.surface, tabs, trackEvent]);

  // Remote tab switches (openDetailTab() from elsewhere on the page).
  React.useEffect(() => {
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent<{ key: string; trigger?: DetailTabTrigger }>).detail;
      const key = detail?.key;
      if (!key || !tabs.some((t) => t.key === key)) return;
      selectTab(key, detail.trigger ?? 'programmatic');
      requestAnimationFrame(() => {
        rootRef.current?.scrollIntoView({
          behavior: reducedMotion ? 'auto' : 'smooth',
          block: 'start',
        });
      });
    };
    window.addEventListener(OPEN_TAB_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_TAB_EVENT, onOpen);
  }, [tabs, reducedMotion, selectTab]);

  if (tabs.length === 0) return null;

  return (
    <Box
      ref={rootRef}
      sx={{
        scrollMarginTop: 96,
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: `${layoutTokens.radius.lg}px`,
        overflow: 'hidden',
        backdropFilter: glassBlur.medium,
        WebkitBackdropFilter: glassBlur.medium,
        boxShadow: '0 10px 32px rgba(0, 0, 0, 0.06)',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          p: { xs: 1, md: 1 },
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Tabs
          value={active}
          onChange={(_, next: string) => selectTab(next, 'tab_click')}
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons={false}
          aria-label="Listing details"
          sx={{
            minHeight: 52,
            p: 0.5,
            borderRadius: `${layoutTokens.radius.md}px`,
            backgroundColor: 'action.hover',
            '& .MuiTabs-indicator': {
              display: 'none',
            },
            '& .MuiTabs-scroller': {
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiTabs-list, & .MuiTabs-flexContainer': {
              alignItems: 'center',
            },
            '& .MuiTab-root': {
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.9rem',
              lineHeight: 1.2,
              height: 44,
              minHeight: 44,
              py: 0,
              px: { xs: 1.5, md: 2 },
              borderRadius: `${layoutTokens.radius.sm}px`,
              color: 'text.secondary',
              transition: theme.transitions.create(
                ['background-color', 'box-shadow', 'color'],
                { duration: theme.transitions.duration.shorter },
              ),
              '&:hover': {
                color: 'text.primary',
                backgroundColor: 'background.paper',
              },
              '&.Mui-selected': {
                color: 'primary.contrastText',
                backgroundColor: 'primary.main',
                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`,
              },
              '&.Mui-focusVisible': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: 2,
              },
            },
          }}
        >
          {tabs.map((t) => (
            <Tab
              key={t.key}
              value={t.key}
              label={t.label}
              id={`detail-tab-${t.key}`}
              aria-controls={detailTabPanelId(t.key)}
            />
          ))}
        </Tabs>
        {/* Edge fade hinting horizontal scroll on mobile. */}
        <Box
          aria-hidden
          sx={{
            display: { xs: 'block', md: 'none' },
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            width: 28,
            pointerEvents: 'none',
            background: (t) =>
              `linear-gradient(to right, transparent, ${t.palette.background.paper})`,
          }}
        />
      </Box>

      {tabs.map((t) => {
        const isActive = t.key === active;
        return (
          <Box
            key={t.key}
            role="tabpanel"
            id={detailTabPanelId(t.key)}
            aria-labelledby={`detail-tab-${t.key}`}
            hidden={!isActive}
            sx={{
              px: { xs: 4, md: 5 },
              py: { xs: 2, md: 3 },
              ...(isActive && !reducedMotion
                ? { animation: `${fadeIn} ${TRANSITION_MS}ms ease` }
                : null),
            }}
          >
            {t.content}
          </Box>
        );
      })}
    </Box>
  );
}
